import { memo, useCallback, useMemo } from 'react';
import {
  CLIP_CUSTOMIZER_LAYOUT_TYPES,
  NEW_ENGINE_SCENE_LAYOUT_LABELS
} from '@goldcast/player-config/src/constants/layouts.constants';
import { LayoutDisableReason, LayoutType } from '@goldcast/player-config/src/types/asset-types';
import { isNewLayoutsMigrated } from '@goldcast/player-config/src/utils/clipUtils';
import { LayoutPopupOption } from '../../../ClipCustomizer/Layout/constants';
import useMagicLayoutSelection from '../useMagicLayoutSelection';
import LayoutThumbnail from './LayoutThumbnail';
import SelectableOptionCard from './SelectableOptionCard';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

function ClipCustomizerLayoutCard({
  magicLayoutOption,
  disabled,
  disabledReason
}: {
  magicLayoutOption: LayoutType;
  disabled: boolean;
  disabledReason: string;
}) {
  const { isActive, handleClick, isDialogOpen, dialogContent, handleConfirm, handleCancel } =
    useMagicLayoutSelection(magicLayoutOption);
  const { clipData } = useClipsContext();

  const optionDetails = CLIP_CUSTOMIZER_LAYOUT_TYPES.find(
    option => option.value === magicLayoutOption
  ) as LayoutPopupOption;

  const cardLabel = useMemo(() => {
    const newEngineLabel = NEW_ENGINE_SCENE_LAYOUT_LABELS[magicLayoutOption];
    return isNewLayoutsMigrated(clipData) && newEngineLabel ? newEngineLabel : optionDetails.label;
  }, [clipData, magicLayoutOption, optionDetails.label]);

  const onCardClick = useCallback(() => {
    if (disabled) {
      if (disabledReason === LayoutDisableReason.UNMIGRATED_LIVE_EVENT_DOESNT_SUPPORT_SPEAKER_LAYOUTS) {
        EventBus.dispatch(CustomEvents.OpenEventMigrationPopup, { source: 'ClipCustomizerMagicLayout' });
      }
      return;
    }
    handleClick();
  }, [disabled, disabledReason, handleClick]);

  return (
    <>
      <SelectableOptionCard
        isActive={isActive}
        disabled={disabled}
        preventNativeDisable
        onClick={onCardClick}
        trackingId={`customizer-magic-layout-card-${magicLayoutOption}`}
        ariaLabel={cardLabel}
        tooltipAnchorId={`magic-layout-card-${magicLayoutOption}`}
        label={cardLabel}
      >
        <LayoutThumbnail layout={magicLayoutOption} />
      </SelectableOptionCard>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        title="Confirm Layout Change"
        content={dialogContent}
        confirmLabel="Continue"
        cancelLabel="Cancel"
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default memo(ClipCustomizerLayoutCard);
